import {
  Container,
  Box,
  Grid,
  Typography,
  makeStyles,
  Chip,
} from "@material-ui/core";
import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";

const BRAND_PURPLE = "#4c004c";
const BRAND_PURPLE_DARK = "#2d002d";
const BRAND_PURPLE_LIGHT = "#9b3b9b";
const BRAND_PURPLE_PALE = "#c77dc7";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    paddingBottom: 60,
  },
  header: {
    background: `linear-gradient(135deg, ${BRAND_PURPLE} 0%, ${BRAND_PURPLE_DARK} 100%)`,
    color: "#ffffff",
    padding: "48px 20px 36px",
    textAlign: "center",
  },
  headerTitle: {
    fontSize: "clamp(1.6rem, 3.5vw, 2.2rem)",
    fontWeight: 700,
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: "0.9rem",
    opacity: 0.85,
    maxWidth: 600,
    margin: "0 auto",
    lineHeight: 1.6,
  },
  intro: {
    background: "#fafafa",
    padding: "20px",
    borderRadius: 12,
    marginTop: 32,
    marginBottom: 28,
    border: `1px solid rgba(76,0,76,0.08)`,
  },
  introText: {
    fontSize: "0.85rem",
    color: "#555",
    lineHeight: 1.7,
  },
  card: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 14,
    height: 280,
    background: BRAND_PURPLE_DARK,
    boxShadow: "0 4px 16px rgba(76,0,76,0.12)",
    border: `1px solid rgba(76,0,76,0.1)`,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 32px rgba(76,0,76,0.22)",
    },
  },
  particlesLayer: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    "& canvas": {
      position: "absolute !important",
      inset: 0,
    },
  },
  cardOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: 16,
    pointerEvents: "none",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
  },
  cardTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#ffffff",
    textShadow: "0 1px 6px rgba(0,0,0,0.5)",
  },
  cardChip: {
    background: "rgba(255,255,255,0.18)",
    color: "#ffffff",
    fontSize: "0.62rem",
    fontWeight: 600,
    height: 22,
    letterSpacing: "0.4px",
    border: "1px solid rgba(255,255,255,0.25)",
    backdropFilter: "blur(6px)",
  },
  cardFooter: {
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.75)",
    lineHeight: 1.4,
    background: "rgba(0,0,0,0.35)",
    backdropFilter: "blur(6px)",
    padding: "6px 10px",
    borderRadius: 8,
    alignSelf: "flex-start",
    pointerEvents: "auto",
  },
  legend: {
    marginTop: 40,
    padding: 24,
    background: "#fafafa",
    borderRadius: 12,
    border: `1px solid rgba(76,0,76,0.08)`,
  },
  legendTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: BRAND_PURPLE,
    marginBottom: 12,
  },
  legendItem: {
    fontSize: "0.8rem",
    color: "#555",
    lineHeight: 1.7,
    marginBottom: 6,
    "& strong": { color: BRAND_PURPLE, fontWeight: 600 },
    "& code": {
      background: "rgba(76,0,76,0.06)",
      color: BRAND_PURPLE,
      padding: "1px 6px",
      borderRadius: 4,
      fontFamily: "monospace",
      fontSize: "0.78rem",
    },
  },
}));

const baseInteractivity = {
  events: { resize: true },
};

const variations = [
  {
    id: "linked",
    title: "1. Linked (current Services)",
    tag: "Baseline",
    hint: "Hover: grab",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: {
        events: {
          onHover: { enable: true, mode: "grab" },
          resize: true,
        },
        modes: { grab: { distance: 140, links: { opacity: 0.5 } } },
      },
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        links: {
          color: BRAND_PURPLE_LIGHT,
          distance: 150,
          enable: true,
          opacity: 0.3,
          width: 1,
        },
        move: { enable: true, speed: 1, outModes: { default: "out" } },
        number: { density: { enable: true, area: 800 }, value: 50 },
        opacity: { value: 0.5 },
        shape: { type: "circle" },
        size: { value: { min: 1, max: 3 } },
      },
      detectRetina: true,
    },
  },
  {
    id: "constellation",
    title: "2. Constellation (dense)",
    tag: "Linked + bounce",
    hint: "Bouncy edges",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        links: {
          color: BRAND_PURPLE_LIGHT,
          distance: 100,
          enable: true,
          opacity: 0.4,
          width: 1,
        },
        move: { enable: true, speed: 0.6, outModes: "bounce" },
        number: { value: 100, density: { enable: true, area: 800 } },
        opacity: { value: 0.6 },
        shape: { type: "circle" },
        size: { value: 2 },
      },
      detectRetina: true,
    },
  },
  {
    id: "stars",
    title: "3. Stars",
    tag: "Shape",
    hint: "5-point stars",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 0.8, outModes: "out", random: true },
        number: { value: 60, density: { enable: true, area: 800 } },
        opacity: { value: 0.7 },
        shape: { type: "star" },
        size: { value: { min: 2, max: 5 } },
      },
      detectRetina: true,
    },
  },
  {
    id: "triangles",
    title: "4. Triangles",
    tag: "Shape",
    hint: "Slow drift",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_LIGHT },
        move: { enable: true, speed: 1.2, outModes: "out" },
        number: { value: 40 },
        opacity: { value: 0.6 },
        shape: { type: "triangle" },
        size: { value: { min: 3, max: 7 } },
        rotate: {
          value: { min: 0, max: 360 },
          animation: { enable: true, speed: 4 },
        },
      },
      detectRetina: true,
    },
  },
  {
    id: "hexagons",
    title: "5. Hexagons",
    tag: "Shape",
    hint: "Polygon, 6 sides",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 1, outModes: "out" },
        number: { value: 35 },
        opacity: { value: 0.5 },
        shape: { type: "polygon", options: { polygon: { sides: 6 } } },
        size: { value: { min: 4, max: 9 } },
        rotate: {
          value: { min: 0, max: 360 },
          animation: { enable: true, speed: 3 },
        },
      },
      detectRetina: true,
    },
  },
  {
    id: "snow",
    title: "6. Snow falling",
    tag: "Preset",
    hint: "Drifts downward",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: "#ffffff" },
        move: {
          enable: true,
          direction: "bottom",
          speed: { min: 1, max: 3 },
          straight: false,
        },
        number: { value: 100 },
        opacity: { value: 0.7 },
        shape: { type: "circle" },
        size: { value: { min: 1, max: 4 } },
      },
      detectRetina: true,
    },
  },
  {
    id: "bubbles",
    title: "7. Bubbles rising",
    tag: "Preset",
    hint: "Floats upward",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, direction: "top", speed: 1.5, outModes: "out" },
        number: { value: 30 },
        opacity: { value: 0.4 },
        shape: { type: "circle" },
        size: {
          value: { min: 6, max: 16 },
          animation: { enable: true, speed: 4 },
        },
      },
      detectRetina: true,
    },
  },
  {
    id: "repulse",
    title: "8. Repulse on hover",
    tag: "Interaction",
    hint: "Flee cursor",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: {
        events: {
          onHover: { enable: true, mode: "repulse" },
          resize: true,
        },
        modes: { repulse: { distance: 100, duration: 0.4 } },
      },
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 1, outModes: "out" },
        number: { value: 60, density: { enable: true, area: 800 } },
        opacity: { value: 0.6 },
        shape: { type: "circle" },
        size: { value: { min: 1, max: 3 } },
      },
      detectRetina: true,
    },
  },
  {
    id: "bubble-hover",
    title: "9. Bubble on hover",
    tag: "Interaction",
    hint: "Inflate near cursor",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: {
        events: {
          onHover: { enable: true, mode: "bubble" },
          resize: true,
        },
        modes: {
          bubble: { distance: 150, size: 12, duration: 2, opacity: 0.9 },
        },
      },
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 0.8, outModes: "out" },
        number: { value: 70 },
        opacity: { value: 0.5 },
        shape: { type: "circle" },
        size: { value: 2 },
      },
      detectRetina: true,
    },
  },
  {
    id: "twinkle",
    title: "10. Twinkle",
    tag: "Effect",
    hint: "Random flicker",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 0.5, outModes: "out" },
        number: { value: 80 },
        opacity: { value: 0.5 },
        shape: { type: "circle" },
        size: { value: 2 },
        twinkle: {
          particles: {
            enable: true,
            frequency: 0.08,
            opacity: 1,
            color: { value: "#ffffff" },
          },
        },
      },
      detectRetina: true,
    },
  },
  {
    id: "click-push",
    title: "11. Click to push",
    tag: "Interaction",
    hint: "Click to add 4",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: {
        events: {
          onClick: { enable: true, mode: "push" },
          resize: true,
        },
        modes: { push: { quantity: 4 } },
      },
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 1.2, outModes: "out" },
        number: { value: 30 },
        opacity: { value: 0.6 },
        shape: { type: "circle" },
        size: { value: { min: 2, max: 4 } },
      },
      detectRetina: true,
    },
  },
  {
    id: "animated-size",
    title: "12. Animated size + opacity",
    tag: "Effect",
    hint: "Pulses",
    options: {
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      interactivity: baseInteractivity,
      particles: {
        color: { value: BRAND_PURPLE_PALE },
        move: { enable: true, speed: 0.6, outModes: "out" },
        number: { value: 40 },
        opacity: {
          value: 0.5,
          animation: { enable: true, speed: 1, sync: false },
        },
        shape: { type: "circle" },
        size: {
          value: { min: 1, max: 6 },
          animation: { enable: true, speed: 3, minimumValue: 0.3 },
        },
      },
      detectRetina: true,
    },
  },
];

export const Test = () => {
  const classes = useStyles();
  const [particlesReady, setParticlesReady] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setParticlesReady(true));
  }, []);

  return (
    <div className={classes.page}>
      <Helmet>
        <title>Particles Playground - Arawinz</title>
      </Helmet>

      <Box className={classes.header}>
        <Typography className={classes.headerTitle}>
          Particles Playground
        </Typography>
        <Typography className={classes.headerSubtitle}>
          12 variations from @tsparticles/slim using the Arawinz purple palette.
          Hover, click, and observe.
        </Typography>
      </Box>

      <Container maxWidth="lg">
        <Box className={classes.intro}>
          <Typography className={classes.introText}>
            Each tile below is an independent tsparticles canvas. Pick the look
            you like, copy its options block from{" "}
            <code style={{ background: "rgba(76,0,76,0.06)", padding: "1px 6px", borderRadius: 4, color: BRAND_PURPLE }}>
              src/pages/user/test.jsx
            </code>{" "}
            into the Services hero, or tell me which one to wire in for you.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {variations.map((v) => (
            <Grid item xs={12} sm={6} md={4} key={v.id}>
              <Box className={classes.card}>
                {particlesReady && (
                  <Particles
                    id={`particles-${v.id}`}
                    className={classes.particlesLayer}
                    options={v.options}
                  />
                )}
                <Box className={classes.cardOverlay}>
                  <Box className={classes.cardHeader}>
                    <Typography className={classes.cardTitle}>
                      {v.title}
                    </Typography>
                    <Chip
                      label={v.tag}
                      size="small"
                      className={classes.cardChip}
                    />
                  </Box>
                  <Typography className={classes.cardFooter}>
                    {v.hint}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box className={classes.legend}>
          <Typography className={classes.legendTitle}>
            Quick reference
          </Typography>
          <Typography className={classes.legendItem}>
            <strong>Shapes:</strong> <code>circle</code>, <code>square</code>,{" "}
            <code>triangle</code>, <code>star</code>, <code>polygon</code>,{" "}
            <code>edge</code>, <code>image</code>, or an array to mix.
          </Typography>
          <Typography className={classes.legendItem}>
            <strong>Hover modes:</strong> <code>grab</code>, <code>bubble</code>
            , <code>repulse</code>, <code>attract</code>, <code>slow</code>,{" "}
            <code>connect</code>.
          </Typography>
          <Typography className={classes.legendItem}>
            <strong>Click modes:</strong> <code>push</code>, <code>remove</code>
            , <code>repulse</code>, <code>bubble</code>, <code>attract</code>,{" "}
            <code>pause</code>, <code>trail</code>.
          </Typography>
          <Typography className={classes.legendItem}>
            <strong>Animatable:</strong> opacity, size, rotate, color, stroke -
            add an <code>animation</code> block to any of them.
          </Typography>
          <Typography className={classes.legendItem}>
            <strong>Movement:</strong> directions (<code>top</code>,{" "}
            <code>bottom</code>, <code>left</code>, <code>right</code>,{" "}
            <code>none</code>), <code>gravity</code>, <code>vibrate</code>,{" "}
            <code>trail</code>, <code>path</code> generators.
          </Typography>
        </Box>
      </Container>
    </div>
  );
};
